import { X } from '@/components/icons';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { useNavigate } from '@tanstack/react-router';
import { useQuickStartGuide } from './useQuickStartGuide';
import { QuickStartStepList, useStepSelection } from './QuickStartStepList';
import { QuickStartStepDrawers } from './QuickStartStepDrawers';
import type { StepId } from './useQuickStartGuide';

export interface QuickStartStep {
  id: string;
  label: string;
  completed: boolean;
}

interface QuickStartGuideDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onStepComplete?: () => void;
  steps?: QuickStartStep[];
}

const defaultSteps: QuickStartStep[] = [
  { id: 'owner-details', label: 'Tell Us about You', completed: false },
  { id: 'aircraft', label: 'Add Your First Aircraft', completed: false },
  { id: 'crew', label: 'Add Your First Crew Member', completed: false },
  { id: 'passenger', label: 'Add a Passenger Profile', completed: false },
  { id: 'roster-entry', label: 'Create Your First Roster Entry', completed: false },
];

export function QuickStartGuideDrawer({
  isOpen,
  onClose,
  onStepComplete,
  steps = defaultSteps,
}: QuickStartGuideDrawerProps) {
  const filteredSteps = steps.filter((s) => s.id !== 'trip');
  const guide = useQuickStartGuide(filteredSteps);
  const navigate = useNavigate();
  const { effectiveSelectedId, selectedStepLabel, setSelectedStepId } = useStepSelection(filteredSteps, guide.firstIncompleteId);
  const handleDrawerClose = () => {
    guide.handleDrawerClose(onStepComplete);
  };

  const handleCta = () => {
    if (effectiveSelectedId) {
      guide.setActiveDrawer(effectiveSelectedId as StepId);
    }
  };

  const handleFinish = () => {
    guide.handleFinish({ showDialog: false });
    onClose();
    navigate({ to: '/operations' });
  };

  const handleCreateTrip = () => {
    guide.handleFinish({ showDialog: false });
    onClose();
    navigate({ to: '/operations/trips', search: { createTrip: true, quickStart: true } });
  };

  return (
    <>
      <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DrawerContent className="bg-surface-L0 flex flex-col max-h-[85dvh]">
          <DrawerHeader className="flex items-center justify-between p-4 shrink-0">
            <DrawerTitle className="text-lg font-bold text-content-primary-bold tracking-[-1px]">
              Quick Start Guide
            </DrawerTitle>
            <button
              type="button"
              onClick={onClose}
              className="p-1 hover:bg-surface-L2 rounded transition-colors"
            >
              <X className="w-4 h-4 text-content-primary-bold" />
            </button>
          </DrawerHeader>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-4 py-2 flex flex-col gap-6 items-center">
            <p className="text-2xl text-content-brand-bold text-center">
              Get Your First Trip Ready
            </p>

            <QuickStartStepList
              steps={filteredSteps}
              activeStepId={effectiveSelectedId}
              onStepClick={setSelectedStepId}
              onStepOpen={(id) => guide.setActiveDrawer(id as StepId)}
            />

            <p className="text-xs text-content-primary-subtle">
              {guide.completedCount} of {guide.totalCount} Complete
            </p>
          </div>

          {/* Footer */}
          <div className="px-4 pt-4 pb-8 shrink-0">
            {guide.allCompleted ? (
              <div className="flex gap-4">
                <Button
                  onClick={handleFinish}
                  variant="secondary"
                  roundness="round"
                  className="flex-1 min-h-9 bg-content-primary-bold text-content-primary-inverse hover:bg-content-primary-bold/90"
                >
                  Finish
                </Button>
                <Button
                  onClick={handleCreateTrip}
                  variant="primary"
                  roundness="round"
                  className="flex-1 min-h-9"
                >
                  Create Your First Trip
                </Button>
              </div>
            ) : (
              <Button
                onClick={handleCta}
                variant="primary"
                roundness="round"
                className="w-full min-h-9"
              >
                {selectedStepLabel}
              </Button>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      <QuickStartStepDrawers
        activeDrawer={guide.activeDrawer}
        onDrawerClose={handleDrawerClose}
        showAllSetDialog={guide.showAllSetDialog}
        onAllSetClose={() => guide.setShowAllSetDialog(false)}
      />
    </>
  );
}
